import React from 'react';
import { PRICING } from '../pricing';
import QuizIcon from '@mui/icons-material/Quiz';
import CalculateIcon from '@mui/icons-material/Calculate';
import CodeIcon from '@mui/icons-material/Code';
import SyncIcon from '@mui/icons-material/Sync';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LayersIcon from '@mui/icons-material/Layers';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

export const javascript = {
    title: "JavaScript Deep Dive",
    subtitle: "Foundation Mastery",
    icon: <CodeIcon sx={{ fontSize: 40, color: '#f7df1e' }} />,
    description: "Master core internals, asynchronous patterns, and modern ES6+ features.",
    features: [
      "Event Loop & V8 Engine Visualized",
      "Closures, Hoisting & Scope Chains",
      "Prototypal Inheritance Deep Dive",
      "Company-Specific TR Patterns"
    ],
    socialProof: {
      name: "Radhika Chopra",
      location: "Delhi",
      time: "5 mins ago",
      viewers: 23
    },
    price: PRICING.javascript.price,
    mrp: PRICING.javascript.mrp,
    topics: [
      {
        id: "js_gec",
        title: "Global Execution Context",
        desc: "The 'Creation Phase' and Global Object internals.",
        badge: "Easy",
        fileUrl: "/documents/javascript/js_gec.md",
        icon: <AccountTreeIcon />,
        group: "JS Core",
        problems: []
      },
      {
        id: "js_hoisting",
        title: "Hoisting & TDZ",
        desc: "Variable vs Function hoisting and the Temporal Dead Zone.",
        badge: "Medium",
        fileUrl: "/documents/javascript/js_hoisting.md",
        icon: <SyncIcon />,
        group: "JS Core",
        problems: []
      },
      {
        id: "js_call_stack",
        title: "The Call Stack",
        desc: "Stack Frames, Frame Management, and LIFO lifecycle.",
        badge: "Medium",
        fileUrl: "/documents/javascript/js_call_stack.md",
        icon: <LayersIcon />,
        group: "JS Core",
        problems: []
      },
      {
        id: "js_closures",
        title: "Mastering Closures",
        desc: "Lexical scope and memory persistence mechanics.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_closures.md",
        icon: <SyncAltIcon />,
        group: "JS Core",
        problems: []
      },
      {
        id: "js_this",
        title: "The 'this' Keyword",
        desc: "Implicit, Explicit, and Lexical binding at runtime.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_this_binding.md",
        icon: <CodeIcon />,
        group: "JS Core",
        problems: []
      },
      {
        id: "js_array_methods",
        title: "Array Mastery",
        desc: "Differentiate and master Map, Filter, and Reduce.",
        badge: "Medium",
        fileUrl: "/documents/javascript/js_array_map.md", // Default first file
        icon: <CalculateIcon />,
        group: "JS Core",
        problems: [
          { id: "js_arr_map", title: "Array.map", badge: "Transform", fileUrl: "/documents/javascript/js_array_map.md" },
          { id: "js_arr_filter", title: "Array.filter", badge: "Evaluate", fileUrl: "/documents/javascript/js_array_filter.md" },
          { id: "js_arr_reduce", title: "Array.reduce", badge: "Accumulate", fileUrl: "/documents/javascript/js_array_reduce.md" }
        ]
      },
      {
        id: "js_functional",
        title: "Functional Patterns",
        desc: "Master Pure Functions, Immutability, and Currying.",
        badge: "Medium",
        fileUrl: "/documents/javascript/js_pure_functions.md", // Default first file
        icon: <CodeIcon />,
        group: "JS Core",
        problems: [
          { id: "js_fn_pure", title: "Pure Functions", badge: "No Side Effects", fileUrl: "/documents/javascript/js_pure_functions.md" },
          { id: "js_fn_hof", title: "Higher-Order Functions", badge: "Callbacks", fileUrl: "/documents/javascript/js_higher_order.md" },
          { id: "js_fn_currying", title: "Currying", badge: "Partial App", fileUrl: "/documents/javascript/js_currying.md" }
        ]
      },
      {
        id: "js_prototypes",
        title: "Prototypes & Inheritance",
        desc: "Master Prototypal Inheritance and the prototype chain.",
        badge: "Medium",
        fileUrl: "/documents/javascript/js_prototypes.md",
        icon: <LayersIcon />,
        group: "Advanced JS",
        problems: []
      },
      {
        id: "js_async",
        title: "Async & Event Loop",
        desc: "Microtasks, Macrotasks, and V8 orchestration.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_event_loop.md", // Default first file
        icon: <SyncIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_async_loop", title: "The Event Loop", badge: "V8 Engine", fileUrl: "/documents/javascript/js_event_loop.md" },
          { id: "js_async_queues", title: "Micro vs Macrotasks", badge: "Queues", fileUrl: "/documents/javascript/js_task_queues.md" },
          { id: "js_async_timer", title: "Timers (setTimeout)", badge: "Web APIs", fileUrl: "/documents/javascript/js_timers.md" },
          { id: "js_async_await", title: "Advanced async/await", badge: "Concurrency", fileUrl: "/documents/javascript/js_async_await.md" }
        ]
      },
      {
        id: "js_promises",
        title: "Mastering Promises",
        desc: "Deep dive into Promise lifecycle and concurrency.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_promises_mastery.md",
        icon: <SyncAltIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_prom_all", title: "Promise.all", badge: "Wait All", fileUrl: "/documents/javascript/js_promise_all.md" },
          { id: "js_prom_race", title: "Promise.race", badge: "First Settled", fileUrl: "/documents/javascript/js_promise_race.md" },
          { id: "js_prom_any", title: "Promise.any", badge: "First Success", fileUrl: "/documents/javascript/js_promise_any.md" },
          { id: "js_prom_allsettled", title: "Promise.allSettled", badge: "Wait All", fileUrl: "/documents/javascript/js_promise_allsettled.md" }
        ]
      },
      {
        id: "js_advanced_patterns",
        title: "Advanced Patterns & Architecture",
        desc: "Senior-level concepts like Debouncing, Memory Copying, Web Workers, and GC.",
        badge: "Expert",
        fileUrl: "/documents/javascript/js_debounce.md", // Default first file
        icon: <SyncAltIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_debounce", title: "Debounce", badge: "Optimize", fileUrl: "/documents/javascript/js_debounce.md" },
          { id: "js_throttle", title: "Throttle", badge: "Optimize", fileUrl: "/documents/javascript/js_throttle.md" },
          { id: "js_memory_copy", title: "Memory Copying", badge: "Heap vs Stack", fileUrl: "/documents/javascript/js_memory_copy.md" },
          { id: "js_event_prop", title: "Event Propagation", badge: "DOM", fileUrl: "/documents/javascript/js_event_propagation.md" },
          { id: "js_event_del", title: "Event Delegation", badge: "DOM", fileUrl: "/documents/javascript/js_event_delegation.md" },
          { id: "js_generators", title: "Generators", badge: "Iterators", fileUrl: "/documents/javascript/js_generators.md" },
          { id: "js_gc", title: "Garbage Collection", badge: "Engine", fileUrl: "/documents/javascript/js_garbage_collection.md" },
          { id: "js_workers", title: "Web Workers", badge: "Threads", fileUrl: "/documents/javascript/js_web_workers.md" },
          { id: "js_design_patterns", title: "JS Design Patterns", badge: "Archi", fileUrl: "/documents/javascript/js_design_patterns.md" }
        ]
      },
      {
        id: "js_core_enhancements",
        title: "Language Internals & Mechanics",
        desc: "Deep knowledge of Object configurations, Type checking, Memory reference passing, and Scoping mechanisms.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_pass_by_value_ref.md",
        icon: <AccountTreeIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_pass_by_value_ref", title: "Pass by Value vs By Reference", badge: "Memory", fileUrl: "/documents/javascript/js_pass_by_value_ref.md" },
          { id: "js_scope", title: "Scope & Lexical Environment", badge: "Scope", fileUrl: "/documents/javascript/js_scope.md" },
          { id: "js_destructuring", title: "Destructuring Assignment", badge: "Syntax", fileUrl: "/documents/javascript/js_destructuring.md" },
          { id: "js_typeof_instanceof", title: "Type checking: typeof & instanceof", badge: "Types", fileUrl: "/documents/javascript/js_typeof_instanceof.md" },
          { id: "js_map_set", title: "Map & Set", badge: "Types", fileUrl: "/documents/javascript/js_map_set.md" },
          { id: "js_object_define_property", title: "Object.defineProperty", badge: "Objects", fileUrl: "/documents/javascript/js_object_define_property.md" },
          { id: "js_object_freeze", title: "Object.freeze & Object.seal", badge: "Objects", fileUrl: "/documents/javascript/js_object_freeze.md" },
          { id: "js_oop_concepts", title: "OOP Concepts in JS", badge: "Architecture", fileUrl: "/documents/javascript/js_oop_concepts.md" },
          { id: "js_async_to_sync", title: "Handling Asynchronicity (Sync execution)", badge: "Async", fileUrl: "/documents/javascript/js_async_to_sync.md" }
        ]
      },
      {
        id: "js_es6_meta",
        title: "ES6+ Meta-Programming",
        desc: "Intercepting behavior, Weak References, and Iterators.",
        badge: "Expert",
        fileUrl: "/documents/javascript/js_proxy_reflect.md",
        icon: <AccountTreeIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_proxy_reflect", title: "Proxy & Reflect API", badge: "Reactivity", fileUrl: "/documents/javascript/js_proxy_reflect.md" },
          { id: "js_weakref", title: "WeakRef & Memory Sweep", badge: "Memory", fileUrl: "/documents/javascript/js_weakref.md" },
          { id: "js_symbols", title: "Symbols & Iterators", badge: "Core API", fileUrl: "/documents/javascript/js_symbols.md" }
        ]
      },
      {
        id: "js_modern_apis",
        title: "Modern Browser APIs",
        desc: "Deep cloning, Internationalization, and Advanced RegEx.",
        badge: "Hard",
        fileUrl: "/documents/javascript/js_structured_clone.md",
        icon: <LayersIcon />,
        group: "Advanced JS",
        problems: [
          { id: "js_structured_clone", title: "structuredClone (Deep Copy)", badge: "Data", fileUrl: "/documents/javascript/js_structured_clone.md" },
          { id: "js_intl_api", title: "Intl API (Localization)", badge: "Format", fileUrl: "/documents/javascript/js_intl_api.md" },
          { id: "js_regex_advanced", title: "Advanced RegEx Patterns", badge: "Parse", fileUrl: "/documents/javascript/js_regex_advanced.md" }
        ]
      },
      {
        id: "js_interview",
        title: "Expert JS Interview",
        desc: "Senior-level output questions with visualizations.",
        badge: "Expert",
        fileUrl: "/documents/javascript/js_interview_questions.md",
        icon: <QuizIcon />,
        group: "Performance",
        problems: []
      },
      {
        id: "js_practice_quizzes",
        title: "JavaScript Practice Quizzes",
        desc: "Consolidated topic-specific assessment to test your mastery.",
        badge: "Quiz",
        icon: <QuizIcon />,
        group: "Final Assessment",
        problems: [
          { id: "faq_output_quiz", title: "Frequently Asked Output Questions", badge: "Interactive", fileUrl: "/documents/interview/quiz_session.md" },
          { id: "quiz_js_gec", title: "Global Execution Context Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_gec.md" },
          { id: "quiz_js_hoisting", title: "Hoisting & TDZ Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_hoisting.md" },
          { id: "quiz_js_call_stack", title: "The Call Stack Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_call_stack.md" },
          { id: "quiz_js_closures", title: "Mastering Closures Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_closures.md" },
          { id: "quiz_js_this", title: "The 'this' Keyword Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_this.md" },
          { id: "quiz_js_array_methods", title: "Array Mastery Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_array_methods.md" },
          { id: "quiz_js_functional", title: "Functional Patterns Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_functional.md" },
          { id: "quiz_js_prototypes", title: "Prototypes & Inheritance Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_prototypes.md" },
          { id: "quiz_js_async", title: "Async & Event Loop Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_async.md" },
          { id: "quiz_js_promises", title: "Mastering Promises Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_promises.md" },
          { id: "quiz_js_advanced_patterns", title: "Advanced Patterns & Architecture Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_advanced_patterns.md" },
          { id: "quiz_js_interview", title: "Expert JS Interview Quiz", badge: "Quiz", fileUrl: "/documents/quizzes/quiz_js_interview.md" }
        ]
      }
    ]
  };
